import { AiCreditBalanceLevel } from "@repo/app-types/entities/creditUsage";
import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import { ButtonVariant, buttonStyle } from "@repo/ui/Buttons/button";
import { iconButton } from "@repo/ui/Buttons/iconButton";
import { Size } from "@repo/ui/helpers/helpers";
import { popoverStyle } from "@repo/ui/popover/style";
import { DateTime } from "luxon";
import type { FC } from "react";
import {
  Button as AriaButton,
  Dialog,
  DialogTrigger,
  Popover,
} from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { useAiCreditStatusQuery } from "../../api/entities/company.js";
import { useTeammateAuthorization } from "../../helpers/teammateAuthorizationContext.js";
import {
  AI_CREDIT_STATUS_POLLING_MS,
  AI_CREDITS_SALES_HREF,
} from "./aiCreditsLimitBanner.js";

export type AiCreditsSurface = "signals" | "automations" | "askAlpharun";

const surfaceDescriptions: Record<AiCreditsSurface, string> = {
  signals:
    "Signals may stop checking new calls until credits reset or more are added.",
  automations:
    "Automations may stop running until credits reset or more are added.",
  askAlpharun:
    "Ask Alpharun may stop answering until credits reset or more are added.",
};

interface AiCreditsExhaustedPopoverProps {
  surface: AiCreditsSurface;
  /** Exclusive ISO end of the current period. */
  resetsAt: string;
  canAddCredits: boolean;
}

/** A page header's used-up AI credit ring and the popover it opens. */
export const AiCreditsExhaustedPopover: FC<AiCreditsExhaustedPopoverProps> = (
  props,
) => {
  const { surface, resetsAt, canAddCredits } = props;
  const resetDate = DateTime.fromISO(resetsAt, { zone: "utc" }).toFormat(
    "MMM d",
  );

  return (
    <DialogTrigger>
      <AriaButton
        aria-label="AI credits used up"
        className={twMerge(
          iconButton({ size: Size.MEDIUM, variant: ButtonVariant.Tertiary }),
          "rounded-full",
        )}
      >
        <span className="relative flex size-8 items-center justify-center">
          <svg aria-hidden="true" className="size-8" viewBox="0 0 32 32">
            <circle
              className="fill-none stroke-red-200"
              cx="16"
              cy="16"
              r="13"
              strokeWidth="2.5"
            />
          </svg>
          <span className="absolute text-[9px] leading-none font-semibold text-red-600 tabular-nums">
            0%
          </span>
        </span>
      </AriaButton>

      <Popover placement="bottom end" className={popoverStyle().popover()}>
        <Dialog
          aria-label="AI credit usage"
          className="w-64 overflow-hidden outline-hidden"
        >
          <div className="flex items-center justify-between gap-4 px-4 pt-4 pb-3">
            <h2 className="text-sm font-semibold text-foreground">
              AI credits used up
            </h2>
            <p className="shrink-0 text-xs text-muted-foreground">
              Resets {resetDate}
            </p>
          </div>

          <div className="border-t border-border-light px-4 pt-3 pb-4">
            <p className="text-xs text-foreground">
              {surfaceDescriptions[surface]}
            </p>
            {canAddCredits ? (
              <a
                className={twMerge(
                  buttonStyle({
                    variant: ButtonVariant.Secondary,
                    size: Size.SMALL,
                  }),
                  "mt-3 w-full justify-center",
                )}
                href={AI_CREDITS_SALES_HREF}
              >
                Contact sales to add credits
              </a>
            ) : (
              <p className="mt-3 text-xs text-muted-foreground">
                Only a billing admin can add credits.
              </p>
            )}
          </div>
        </Dialog>
      </Popover>
    </DialogTrigger>
  );
};

interface AiCreditsExhaustedIndicatorProps {
  surface: AiCreditsSurface;
}

/** The used-up AI credit indicator for a credit-using page's header. */
export const AiCreditsExhaustedIndicator: FC<
  AiCreditsExhaustedIndicatorProps
> = (props) => {
  const authorization = useTeammateAuthorization();
  const { data: status } = useAiCreditStatusQuery(undefined, {
    pollingInterval: AI_CREDIT_STATUS_POLLING_MS,
  });

  if (status?.level !== AiCreditBalanceLevel.Exhausted) {
    return null;
  }
  return (
    <AiCreditsExhaustedPopover
      canAddCredits={authorization.can(
        PermissionActions.UPDATE,
        PermissionSubjects.BILLING,
      )}
      resetsAt={status.resets_at}
      surface={props.surface}
    />
  );
};
